import dayjs from "dayjs";
import { useState, useEffect } from "react";
import { Button, Checkbox, Typography, Card, Divider } from "antd";
import { useLoader } from "../../../../Context/LoaderContext/LoaderContext";
import InstructionList from "./InstructionList";
import {
  DesktopOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  BellOutlined,
  TrophyOutlined,
  FormOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { getDeviceType } from "../../../../Utils/generic-functions";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentStart from "../../hooks/useAiAssessmentStart";
import AIAssessmentHelpModal from "../aiAssessmentHelpModal/AIAssessmentHelpModal";
import { useAppContext } from "../../../../Context/AppContext/AppContext";
import AuthService from "../../../../Services/auth.service";
import { UserOutlined } from "@ant-design/icons";
import { useCurrentApplication } from "../../../../Context/CurrentApplicationContext/CurrentApplicationContext";
import ReminderModal from "./ReminderModal";
import { InterviewSession } from "../../../../Entities/interview-session.entity";
import { NotificationReminder } from "../../../../Interface/Index";
import notificationReminderService from "../../../../Services/notification.reminder.service";
import useBrowserDetection from "../DeviceSetupWizard/hooks/useBrowserDetection";
import "./instruction.scss";

interface InstructionsProps {
  interviewSession: InterviewSession | null;
}

const Instructions: React.FC<InstructionsProps> = ({ interviewSession }) => {
  const { isLoading } = useLoader();
  const [accepted, setAccepted] = useState(true);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [isAiAssessmentHelpModalOpen, setIsAiAssessmentHelpModalOpen] =
    useState(true);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [existingReminder, setExistingReminder] =
    useState<NotificationReminder | null>(null);

  const { jobPost, jobApplication } = useCurrentApplication();

  const { currentSession, aiAssessmentDetails } = useAIAssessmentContext();
  const { organization } = useAppContext();
  const themeColor = organization?.theme?.primaryColor || "#1890ff";
  const positionLabel = jobPost?.position || jobPost?.title || "";

  const { handleStartAiAssessment } = useAiAssessmentStart();

  const { browser } = useBrowserDetection();
  const isFirefox = browser === "firefox";

  const authService = AuthService.getInstance();
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    const deviceType = getDeviceType();
    setIsMobileOrTablet(deviceType === "mobile" || deviceType === "tablet");
  }, []);

  useEffect(() => {
    if (!interviewSession?._id) return;
    notificationReminderService
      .getRemindersBySessionId(interviewSession._id)
      .then((reminders) => {
        const first = reminders?.[0] ?? null;
        setExistingReminder(first);
      });
  }, [interviewSession?._id]);

  const handleAcceptClick = (e: any) => {
    setAccepted(e.target.checked);
  };

  const handleOpenAiAssessmentHelpModal = () => {
    setIsAiAssessmentHelpModalOpen(true);
  };

  const handleRemindMe = () => {
    setShowReminderModal(true);
  };

  const mobilePromptMessage = () => {
    return (
      <div className="mobile-prompt-content">
        <div className="mobile-prompt-header">
          <div className="mobile-prompt-icon">
            <DesktopOutlined />
          </div>
          <Typography.Title
            level={4}
            style={{
              textAlign: "center",
              margin: 0,
            }}
          >
            Desktop Required
          </Typography.Title>
        </div>

        <div style={{ textAlign: "center" }}>
          <Typography.Text>
            Mobile and tablet devices are not supported for attending or
            starting the AI Assessment round. Please switch to a desktop device
            to continue.
          </Typography.Text>
        </div>
      </div>
    );
  };

  const firefoxPromptMessage = () => {
    const browsers = [
      { name: "Google Chrome", recommended: true },
      { name: "Microsoft Edge", recommended: false },
      { name: "Safari", recommended: false },
    ];

    return (
      <div className="browser-unsupported-content">
        <div className="browser-unsupported-header">
          <div className="browser-unsupported-icon">
            <GlobalOutlined />
          </div>
          <Typography.Title level={4} style={{ textAlign: "center", margin: 0 }}>
            Firefox is Not Supported
          </Typography.Title>
        </div>

        <Typography.Text style={{ textAlign: "center", display: "block" }}>
          This AI assessment requires features that are not supported in Firefox.
          Please switch to one of the browsers below to continue.
        </Typography.Text>

        <div className="browser-unsupported-list">
          {browsers.map((b) => (
            <div
              key={b.name}
              className={`browser-unsupported-item${b.recommended ? " browser-unsupported-item--recommended" : ""}`}
            >
              <Typography.Text strong>
                {b.name}
              </Typography.Text>
              {b.recommended && (
                <span className="browser-unsupported-badge">Recommended</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isMobileOrTablet) {
    return mobilePromptMessage();
  }

  if (isFirefox) {
    return firefoxPromptMessage();
  }

  const isStartButtonDisabled =
    !accepted ||
    currentSession?.status !== "pending" ||
    currentSession?.isLive ||
    isMobileOrTablet;

  return (
    <div className="instruction_container">
      <div className="instruction_two_column_layout">
        {/* Left Column - Instructions */}
        <div className="instruction_main_content">
          <div className="instruction_header_section">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Important Instructions
            </Typography.Title>
            <Typography.Text type="secondary">
              Please read all instructions carefully before starting the
              assessment
            </Typography.Text>
          </div>
          <div className="instruction_list_wrapper">
            <InstructionList />
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <aside className="instruction_sidebar">
          <Card className="instruction_sidebar_card" bordered={false}>
            {/* Assessment Info */}
            <div className="sidebar_info_section">
              {positionLabel && (
                <div className="info_item">
                  <div
                    className="info_icon"
                    style={{
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                    }}
                  >
                    <UserOutlined />
                  </div>
                  <div className="info_content">
                    <Typography.Text type="secondary" className="info_label">
                      Position
                    </Typography.Text>
                    <Typography.Text strong className="info_value">
                      {positionLabel}
                    </Typography.Text>
                  </div>
                </div>
              )}

              {aiAssessmentDetails?.roundName && (
                <div className="info_item">
                  <div
                    className="info_icon"
                    style={{
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                    }}
                  >
                    <TrophyOutlined />
                  </div>
                  <div className="info_content">
                    <Typography.Text type="secondary" className="info_label">
                      Interview Round
                    </Typography.Text>
                    <Typography.Text strong className="info_value">
                      {aiAssessmentDetails.roundName}
                    </Typography.Text>
                  </div>
                </div>
              )}

              {aiAssessmentDetails?.answerType && (
                <div className="info_item">
                  <div
                    className="info_icon"
                    style={{
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                    }}
                  >
                    <FormOutlined />
                  </div>
                  <div className="info_content">
                    <Typography.Text type="secondary" className="info_label">
                      Question Type
                    </Typography.Text>
                    <Typography.Text
                      strong
                      className="info_value"
                      style={{ textTransform: "capitalize" }}
                    >
                      {aiAssessmentDetails.answerType === "mcq"
                        ? "Multiple Choice"
                        : aiAssessmentDetails.answerType}
                    </Typography.Text>
                  </div>
                </div>
              )}

              {currentSession?.estimatedTime && (
                <div className="info_item">
                  <div
                    className="info_icon"
                    style={{
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                    }}
                  >
                    <ClockCircleOutlined />
                  </div>
                  <div className="info_content">
                    <Typography.Text type="secondary" className="info_label">
                      Estimated Duration
                    </Typography.Text>
                    <Typography.Text strong className="info_value">
                      {currentSession?.estimatedTime} minutes
                    </Typography.Text>
                  </div>
                </div>
              )}

              {aiAssessmentDetails?.noOfQuestions && (
                <div className="info_item">
                  <div
                    className="info_icon"
                    style={{
                      backgroundColor: `${themeColor}15`,
                      color: themeColor,
                    }}
                  >
                    <FileTextOutlined />
                  </div>
                  <div className="info_content">
                    <Typography.Text type="secondary" className="info_label">
                      Total Questions
                    </Typography.Text>
                    <Typography.Text strong className="info_value">
                      {aiAssessmentDetails.noOfQuestions} questions
                    </Typography.Text>
                  </div>
                </div>
              )}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            {/* Remind Me - only show if assessment is pending */}
            {currentSession?.status === "pending" && (
              existingReminder?.status === "pending" ? (
                <div className="reminder_set_info">
                  <BellOutlined className="reminder_set_icon" />
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Reminder set for{" "}
                      <Typography.Text strong style={{ fontSize: 12 }}>
                        {dayjs(existingReminder.scheduleAt).format("D MMM YYYY, hh:mm A")}
                      </Typography.Text>
                    </Typography.Text>
                    <br />
                    <Typography.Link onClick={handleRemindMe} style={{ fontSize: 12 }}>
                      Click here to edit
                    </Typography.Link>
                  </div>
                </div>
              ) : (
                <Button
                  type="default"
                  size="large"
                  icon={<BellOutlined />}
                  onClick={handleRemindMe}
                  block
                  className="remind_button"
                  style={{ marginTop: 8 }}
                >
                  Remind me later
                </Button>
              )
            )}

            <Divider style={{ margin: "16px 0" }} />

            {/* Consent & CTA Section */}
            <div className="consent_section">
              <Checkbox
                onChange={handleAcceptClick}
                checked={accepted}
                className="consent_checkbox"
              >
                <Typography.Text className="consent_text">
                  I have read, understood, and agree to abide by all the
                  instructions and policies listed above.
                </Typography.Text>
              </Checkbox>
            </div>

            <Button
              type="primary"
              onClick={handleStartAiAssessment}
              loading={isLoading}
              disabled={isStartButtonDisabled}
              size="large"
              block
              icon={<PlayCircleOutlined />}
              className={`start_assessment_btn ${!isStartButtonDisabled ? "start_assessment_btn--shine" : ""}`}
              style={{
                backgroundColor: isStartButtonDisabled ? undefined : themeColor,
                borderColor: isStartButtonDisabled ? undefined : themeColor,
                marginTop: 16,
              }}
            >
              Setup Device
            </Button>
          </Card>
        </aside>
      </div>

      <AIAssessmentHelpModal
        isOpen={isAiAssessmentHelpModalOpen}
        setIsOpen={setIsAiAssessmentHelpModalOpen}
      />

      {currentUser && interviewSession && interviewSession.sessionExpiresAt && jobApplication?._id && (
        <ReminderModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          assessmentExpiry={interviewSession.sessionExpiresAt}
          user={currentUser}
          jobApplicationId={jobApplication._id}
          sessionId={interviewSession._id}
          mode={existingReminder?.status === "pending" ? "edit" : "create"}
          existingReminder={existingReminder}
          onSuccess={(reminder) => setExistingReminder(reminder)}
        />
      )}
    </div>
  );
};

export default Instructions;
